import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from '../../services/api';
import SidebarPanel from './SidebarPanel';
import LHCFormEditor from '../forms/LHCFormEditor';
import SurveyJSFormEditor from '../forms/SurveyJSFormEditor';

/**
 * FormManagementSidebar - Sidebar for creating/editing forms in FormsManagementPage
 *
 * @param {boolean} isOpen - Whether sidebar is open
 * @param {Function} onClose - Callback when sidebar closes
 * @param {Object} editingForm - Form being edited (null for new forms)
 * @param {Function} onSave - Callback after successful save
 */
const FormManagementSidebar = ({ isOpen, onClose, editingForm, onSave }) => {
  const [formTypes, setFormTypes] = useState([]);
  const [healthSystems, setHealthSystems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type_id: '',
    description: '',
    health_systems: [],
    schema: {},
    theme: null,
    create_new_version: true
  });
  const [schemaType, setSchemaType] = useState('lhc');
  const surveyJSEditorRef = useRef(null);
  const lhcEditorRef = useRef(null);

  // Fetch form types and health systems when sidebar opens
  useEffect(() => {
    if (isOpen) {
      fetchFormCreationData();
    }
  }, [isOpen]);

  // Load form data when editing
  useEffect(() => {
    if (isOpen && editingForm) {
      // If schema is missing, fetch full form details
      if (!editingForm.schema) {
        fetchFullFormDetails(editingForm.form_id, editingForm.version);
      } else {
        loadFormData(editingForm);
      }
    } else if (isOpen && !editingForm) {
      // Reset for new form
      setFormData({
        name: '',
        type_id: '',
        description: '',
        health_systems: [],
        schema: {},
        theme: null,
        create_new_version: true
      });
      setSchemaType('lhc');
      setError(null);
    }
  }, [isOpen, editingForm]);

  const fetchFormCreationData = async () => {
    try {
      setIsLoading(true);
      const [typesResponse, healthSystemsResponse] = await Promise.all([
        adminApi.getFormTypes(),
        adminApi.getHealthSystems()
      ]);

      if (typesResponse.data.success) {
        const fetchedFormTypes = typesResponse.data.form_types;
        if (fetchedFormTypes.length === 0) {
          setFormTypes([
            { id: '1', name: 'LHC form' },
            { id: '2', name: 'Non-LHC form' },
          ]);
        } else {
          setFormTypes(fetchedFormTypes);
        }
      }

      if (healthSystemsResponse.data.success) {
        setHealthSystems(healthSystemsResponse.data.health_systems);
      }
    } catch (err) {
      console.error('Error fetching form creation data:', err);
      setError(`Error loading data: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFullFormDetails = async (formId, version) => {
    try {
      setIsLoading(true);
      const response = await adminApi.getFormDetails(formId, version);

      if (response.data.success) {
        const fullForm = response.data.form;
        loadFormData(fullForm);
      } else {
        setError('Failed to load form details');
      }
    } catch (err) {
      console.error('Error fetching form details:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFormData = (form) => {
    console.log('Loading form data. Theme from API:', form.theme);
    setFormData({
      name: form.name,
      type_id: form.type?.id || form.type_id || '',
      description: form.description || '',
      health_systems: Array.isArray(form.health_systems)
        ? form.health_systems.map(hs => typeof hs === 'object' ? hs.id : hs)
        : [],
      schema: form.schema || {},
      theme: form.theme || null,
      version: form.version,
      create_new_version: false // Default to updating existing version, not creating new one
    });

    const typeName = form.type?.name || form.type || '';
    // Check if type name is "LHC form" (not "Non-LHC form")
    const isLHC = typeName.toLowerCase() === 'lhc form' || typeName.toLowerCase().startsWith('lhc ');
    setSchemaType(isLHC ? 'lhc' : 'custom');
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    if (type === 'select-multiple') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({
        ...formData,
        [name]: selectedOptions
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSchemaChange = (newSchema) => {
    setFormData({
      ...formData,
      schema: newSchema
    });
  };

  const handleEditorError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Get the current schema and theme from the editor ref
      let currentSchema = formData.schema;
      let currentTheme = formData.theme;

      if (schemaType === 'custom' && surveyJSEditorRef.current) {
        currentSchema = surveyJSEditorRef.current.getSchema();
        currentTheme = surveyJSEditorRef.current.getTheme();
      } else if (schemaType === 'lhc' && lhcEditorRef.current && lhcEditorRef.current.getSchema) {
        currentSchema = lhcEditorRef.current.getSchema();
      }

      // Sync title and description from SurveyJS schema if available
      let updatedFormData = { ...formData, schema: currentSchema, theme: currentTheme };
      if (schemaType === 'custom' && currentSchema) {
        if (currentSchema.title) {
          updatedFormData.name = currentSchema.title;
        }
        if (currentSchema.description) {
          updatedFormData.description = currentSchema.description;
        }
      }

      // Validate form data
      if (!updatedFormData.name || !updatedFormData.type_id || updatedFormData.health_systems.length === 0) {
        setError('Please fill out all required fields');
        setIsSaving(false);
        return;
      }

      // Validate schema
      if (!currentSchema || Object.keys(currentSchema).length === 0) {
        setError('Please provide a valid form schema');
        setIsSaving(false);
        return;
      }

      let response;

      if (editingForm) {
        const formDataToSubmit = {
          ...updatedFormData,
          create_new_version: !!updatedFormData.create_new_version
        };
        response = await adminApi.updateForm(editingForm.form_id, formDataToSubmit, updatedFormData.version);
      } else {
        response = await adminApi.createForm(updatedFormData);
      }

      if (response.data.success) {
        if (onSave) onSave();
        onClose();
      } else {
        setError(response.data.message || 'Failed to save form');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      if (err.response && err.response.status === 403) {
        setError('Permission denied. This feature requires proper authentication and authorization.');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidebarPanel
      isOpen={isOpen}
      onClose={onClose}
      title={editingForm ? 'Edit Form' : 'Create New Form'}
      style={{ width: isFullWidth ? '98vw' : '40vw' }}
      headerActions={
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsFullWidth(!isFullWidth);
          }}
          title={isFullWidth ? 'Collapse' : 'Expand to full width'}
        >
          <i className={`fas fa-${isFullWidth ? 'compress' : 'expand'}`}></i>
        </button>
      }
    >
      {isLoading ? (
        <div className="d-flex justify-content-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div>
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="collapsible-form-fields" style={{ display: isFullWidth ? 'none' : 'block' }}>
          <div className="mb-3">
            <label htmlFor="formName" className="form-label">Form Name</label>
            <input
              type="text"
              className="form-control"
              id="formName"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>

          {!editingForm && (<div className="mb-3">
            <label htmlFor="formType" className="form-label">Form Type</label>
            <select
              className="form-select"
              id="formType"
              name="type_id"
              value={formData.type_id}
              onChange={handleInputChange}
              disabled={!!editingForm}
            >
              <option value="">Select Type</option>
              {formTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div> )}

          <div className="mb-3">
            <label htmlFor="formDescription" className="form-label">Description</label>
            <textarea
              className="form-control"
              id="formDescription"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
            ></textarea>
          </div>

          <div className="mb-3">
            <label htmlFor="healthSystems" className="form-label">Health Systems</label>
            <select
              multiple
              className="form-select"
              id="healthSystems"
              name="health_systems"
              value={formData.health_systems}
              onChange={handleInputChange}
            >
              {healthSystems.map(hs => (
                <option key={hs.id} value={hs.id}>{hs.name}</option>
              ))}
            </select>
            <div className="form-text">Hold Ctrl (Windows) or Cmd (Mac) to select multiple.</div>
          </div>

          <div className="mb-3">
            <label className="form-label">Schema Type</label>
            <div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="schemaType"
                  id="lhcSchema"
                  value="lhc"
                  checked={schemaType === 'lhc'}
                  onChange={() => setSchemaType('lhc')}
                  disabled={!!editingForm}
                />
                <label className="form-check-label" htmlFor="lhcSchema">LHC Form</label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="schemaType"
                  id="customSchema"
                  value="custom"
                  checked={schemaType === 'custom'}
                  onChange={() => setSchemaType('custom')}
                  disabled={!!editingForm}
                />
                <label className="form-check-label" htmlFor="customSchema">Custom Form (SurveyJS)</label>
              </div>
            </div>
          </div>
        </div>

          {/* Use editor components based on schema type */}
          {schemaType === 'lhc' ? (
            <LHCFormEditor
              ref={lhcEditorRef}
              schema={formData.schema}
              onChange={handleSchemaChange}
              onError={handleEditorError}
            />
          ) : (
            <SurveyJSFormEditor
              ref={surveyJSEditorRef}
              schema={formData.schema}
              theme={formData.theme}
            />
          )}

          {editingForm && (
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="createNewVersion"
                name="create_new_version"
                checked={formData.create_new_version}
                onChange={(e) => setFormData({ ...formData, create_new_version: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="createNewVersion">Create New Version</label>
              <div className="form-text">If unchecked, the existing version will be updated.</div>
            </div>
          )}

          <div className="d-flex">
            <button type="button" className="btn btn-primary me-2" onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                'Save Form'
              )}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      )}
    </SidebarPanel>
  );
};

export default FormManagementSidebar;
